import * as React from 'react';
import styled from 'styled-components';

import { BaseTile } from './Tile';

import { EmailIcon } from '../../../ui/icons/Email';
import { useProgressiveImage } from '../../../utils/hooks/useProgressiveImage';
import { FacebookIcon } from '../../../ui/icons/Facebook';
import { LinkedInIcon } from '../../../ui/icons/Linkedin';
import { TwitterIcon } from '../../../ui/icons/Twitter';

interface IBrokerItem {
  name?: string | null;
  image?: string | null;
  path?: string | null;
  phone?: string | null;
  title?: string | null;
  office?: string | null;
  urltag?: string | null;
}

export const BrokerSoldItem: React.FC<IBrokerItem> = React.memo(
  ({ name, image, path, phone, title, office, urltag }) => {
    const [share, setShare] = React.useState(false);

    const brokerPath = `https://privatmegleren.no/megler/${path}`;

    return (
      <StyledBrokerItem className="soldprice">
        <StyledBrokerInsideRow className="top-content">
          <BrokerImage url={image || ''} className="soldprice" />
          <StyledBrokerInfo className="soldprice">
            <a rel="noopener" href={brokerPath} className="name">
              {name}
            </a>
            <span className="title">{title}</span>
            <a
              href={`https://privatmegleren.no/${urltag}`}
              target="_blank"
              rel="noopener"
              className="office"
            >
              PrivatMegleren {office}
            </a>
          </StyledBrokerInfo>
          <StyledBrokerNav className="hide-nav">
            <a href={`tel:${phone?.replace(/ /g, '')}`}>{phone}</a>
            <a rel="noopener" href={`${brokerPath}/kontakt`}>
              Send e-post
            </a>
            <a rel="noopener" href={`${brokerPath}/vcard`}>
              Lagre visittkort
            </a>
            <a
              className={share ? 'active' : ''}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShare(!share);
              }}
            >
              Del visittkort
            </a>
          </StyledBrokerNav>
        </StyledBrokerInsideRow>
        <StyledBrokerInsideRow className="bottom-content">
          <StyledBrokerBottom>
            <StyledBrokerNav className="show-nav nav-row">
              <a href={`tel:${phone?.replace(/ /g, '')}`}>{phone}</a>
              <a rel="noopener" href={`${brokerPath}/kontakt`}>
                Send e-post
              </a>
              <a rel="noopener" href={`${brokerPath}/vcard`}>
                Lagre visittkort
              </a>
              <a
                className={share ? 'active' : ''}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShare(!share);
                }}
              >
                Del visittkort
              </a>
            </StyledBrokerNav>
            {share ? (
              <SharingWrapper>
                <div
                  className="icon-button"
                  onClick={() => {
                    window.open(
                      `mailto:?subject=${name}, ${title} - PrivatMegleren&body=https://privatmegleren.no/megler/${path}`,
                      '_blank'
                    );
                  }}
                >
                  <EmailIcon />
                </div>
                <div
                  className="icon-button"
                  onClick={() => {
                    window.open(
                      `https://www.facebook.com/sharer/sharer.php?u=https://privatmegleren.no/megler/${path}`,
                      '_blank'
                    );
                  }}
                >
                  <FacebookIcon />
                </div>
                <div
                  className="icon-button"
                  onClick={() => {
                    window.open(
                      `https://www.linkedin.com/shareArticle?mini=true&url=https://privatmegleren.no/megler/${path}`,
                      '_blank'
                    );
                  }}
                >
                  <LinkedInIcon />
                </div>
                <div
                  className="icon-button"
                  onClick={() => {
                    window.open(
                      `https://twitter.com/home?status=https://privatmegleren.no/megler/${path}`,
                      '_blank'
                    );
                  }}
                >
                  <TwitterIcon />
                </div>
              </SharingWrapper>
            ) : null}
          </StyledBrokerBottom>
        </StyledBrokerInsideRow>
      </StyledBrokerItem>
    );
  }
);

const BrokerImage: React.FC<{ url: string; className: string }> = React.memo(
  ({ url, className }) => {
    const ref = React.useRef(null);

    const { loaded } = useProgressiveImage({
      url: url,
    });

    return (
      <StyledBrokerImage ref={ref} className={className}>
        <img
          alt="meglerbilde"
          data-url={url}
          className={loaded ? 'show' : ''}
          src={loaded ? url : ''}
        />
      </StyledBrokerImage>
    );
  }
);

const StyledBrokerItem = styled(BaseTile)`
  display: flex;
  flex-flow: column;
  padding: 0;
`;

const StyledBrokerInsideRow = styled.div`
  &.top-content {
    display: grid;
    grid-template-columns: 150px 1fr 120px;
    align-items: center;

    @media all and (max-width: 1530px) {
      grid-template-columns: 150px 1fr;
      .hide-nav {
        display: none;
      }
    }
  }
  &.bottom-content {
    .show-nav {
      display: none;
    }
    @media all and (max-width: 1530px) {
      .show-nav {
        display: grid;
      }
    }
  }
`;

const StyledBrokerImage = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  background: rgb(40, 40, 40);
  box-shadow: 0 0 6px 4px black inset;
  background-position: 0 -13px;
  overflow: hidden;

  &.soldprice {
    margin: 20px 0;
    width: 114px;
    height: 114px;
    justify-self: center;

    @media all and (max-width: 1600px) {
      width: 105px;
      height: 105px;
    }
  }

  img {
    transition: opacity 0.2s ease-in-out;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    object-fit: cover;

    &.show {
      opacity: 1;
    }
  }
  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    box-shadow: 0 0 6px 4px black inset;
  }

  @media all and (max-width: 960px) {
    justify-self: center;
  }
`;

const StyledBrokerInfo = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;

  &.soldprice {
    padding: 20px 20px 20px 0;
  }

  .title {
    color: ${({ theme }) => theme.tile.broker.title};
  }
  .office {
    color: ${({ theme }) => theme.tile.broker.link};
  }

  .name {
    font-size: 1.2em;

    @media all and (max-width: 960px) {
      font-family: 'DomaineDisp', sans-serif;
      font-size: 2em;
    }
  }

  a {
    color: ${({ theme }) => theme?.colors?.primary};
    text-decoration: none;
    margin: 0 0 0.3em;
  }
`;

const StyledBrokerNav = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  height: 100%;

  &.nav-row {
    border-bottom: solid 1px rgb(0, 0, 0);
    grid-template-columns: 1fr 1fr 1fr 1fr;

    a {
      border-bottom: none;
    }
    @media all and (max-width: 1315px) {
      grid-template-columns: 1fr 1fr;
      border-bottom: solid 1px rgb(0, 0, 0);

      a {
        border-bottom: solid 1px rgba(0, 0, 0);
        &:nth-of-type(3) {
          border-bottom: none;
        }
      }
    }
  }

  a {
    display: flex;
    padding: 0.75em 0.75em;
    align-items: center;
    height: 100%;
    justify-content: center;
    text-decoration: none;
    text-align: center;

    font-size: 12px;
    color: rgb(255, 255, 255);

    border-bottom: solid 1px rgb(0, 0, 0);
    border-left: solid 1px rgb(0, 0, 0);

    &:last-child {
      border-bottom: none;
    }

    &.active {
      background: #242627;
      color: ${({ theme }) => theme.colors.primary};
    }

    &:hover {
      background: ${({ theme }) => theme.colors.primary};
      color: rgb(0, 0, 0);
    }
  }
`;

const StyledBrokerBottom = styled.div`
  width: 100%;
  border-top: solid 1px rgb(0, 0, 0);
`;

const SharingWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  width: 100%;
  height: auto;
  align-items: center;

  .icon-button {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 20px;

    svg {
      width: 25px;
      height: 25px;

      path {
        fill: ${({ theme }) => theme.colors.primary};
      }
    }

    &:hover {
      background: #242627;
      cursor: pointer;
    }
  }
`;
